import React, { useEffect, useState } from 'react';
import { Box, CircularProgress, SxProps, Typography } from '@mui/material';
import { ChoiceItemList, type ChoiceItemListProps } from '../../molecules/ChoiceItemList';
import { ChoiceItem, ChoiceItemProps, FlexyButton } from '../../atoms';
import { ChoiceListSearch } from '../../molecules/ChoiceListSearch/ChoiceListSearch';

type FormData = {
    namegroup: string;
    idsSociete: string[];
};
export type ChoiceListSubmitAction = {
    label: string;
    onSubmit: (data: FormData) => Promise<void>;
};

export type ChoiceListProps = {
    title: string;
    description: string;
    submitButtonLabel: string;
    placeholderLabelSearch?: string;
    listChoice: Array<ChoiceItemListProps>;
    multiple: boolean;
    defaultValues: string[];
    componentAdd?: React.ReactNode;
    onSubmit: (data: FormData) => void;
    search: (string: string) => void;
    onChange?: (selectedValues: Array<string>) => void;
    noSubmitButton?: boolean;
    invisibleCheckbox?: boolean;
    submitActions?: ChoiceListSubmitAction[];
    sxWrapper?: SxProps;
    maxHeightList?: string;
};

export const ChoiceList: React.FunctionComponent<ChoiceListProps> = function ({
    title,
    description,
    submitButtonLabel,
    placeholderLabelSearch,
    listChoice,
    multiple,
    defaultValues = [],
    componentAdd = null,
    onSubmit,
    search,
    onChange,
    noSubmitButton = false,
    invisibleCheckbox = false,
    submitActions,
    sxWrapper,
    maxHeightList = '400px',
}) {
    const [searchValue, setSearchValue] = React.useState<string>('');
    const searchWrapper: (searchString: string) => void = (searchString) => {
        search(searchString);
        setSearchValue(searchString);
    };

    const [selectedValues, setSelectedValues] = useState<Array<string>>(defaultValues);
    const [loadingAction, setLoadingAction] = useState<string | null>(null);

    const handleChange = (value: string) => {
        setSelectedValues((currentUpdatedValues) => {
            let updatedValues: Array<string>;
            if (currentUpdatedValues.includes(value)) {
                updatedValues = currentUpdatedValues.filter((v) => v !== value); // Déselectionne si déjà sélectionné
            } else if (multiple) {
                updatedValues = [...currentUpdatedValues, value]; // ajout multiple
            } else {
                updatedValues = [value]; // ajout simple
            }
            if (onChange) {
                onChange(updatedValues);
            }
            return updatedValues;
        });
    };
    const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        onSubmit({ namegroup: 'selectionsociete', idsSociete: selectedValues });
    };

    useEffect(() => {
        setSelectedValues(defaultValues);
    }, [defaultValues]);

    return (
        <Box>
            <form onSubmit={handleSubmit}>
                <Box
                    sx={{
                        width: '100%',
                        height: '600px',
                        overflowY: 'scroll',
                        ...sxWrapper,
                    }}
                >
                    <ChoiceListSearch
                        title={title}
                        description={description}
                        placeholderLabelSearch={placeholderLabelSearch}
                        searchValue={searchValue}
                        setSearchValue={searchWrapper}
                    />
                    <Box
                        className="all-list"
                        sx={[
                            {
                                width: '100%',
                                marginBottom: '8px',
                            },
                        ]}
                    >
                        {selectedValues.length !== 0 ? (
                            <>
                                <Typography
                                    sx={{
                                        paddingLeft: '15px',
                                        paddingBottom: '8px',
                                    }}
                                    variant="l2dcaption"
                                    color="text.secondary"
                                >
                                    Société Sélectionnée
                                </Typography>
                                {(
                                    listChoice.reduce(
                                        (
                                            acc: Array<ChoiceItemProps>,
                                            curr: ChoiceItemListProps,
                                        ) => {
                                            acc.push(
                                                ...curr.listChoice.map((innerListChoice) => ({
                                                    ...innerListChoice,
                                                    updateItem: curr.updateItem,
                                                })),
                                            );
                                            return acc;
                                        },
                                        [] as Array<
                                            ChoiceItemProps & {
                                                updateItem?: (value: string) => void;
                                            }
                                        >,
                                    ) as Array<ChoiceItemProps>
                                )
                                    .filter((item) => selectedValues.indexOf(item.value) !== -1)
                                    .map((item) => (
                                        <ChoiceItem
                                            label={item.label}
                                            idFiscal={item.idFiscal}
                                            description={item.description}
                                            onChange={handleChange}
                                            isChecked
                                            sx={{ width: 'calc(100% - 25px)' }}
                                            value={item.value}
                                            isDisabled={false}
                                            updateItem={item.updateItem}
                                            invisibleCheckbox={invisibleCheckbox}
                                        />
                                    ))}
                            </>
                        ) : null}

                        {listChoice.map((choiceList) => (
                            <ChoiceItemList
                                {...choiceList}
                                sx={{ paddingRight: '25px' }}
                                key={choiceList.label}
                                listGroupement={
                                    choiceList.listGroupement ? choiceList.listGroupement : []
                                }
                                multiple={multiple}
                                onChange={handleChange}
                                selectedValues={selectedValues}
                                invisibleCheckbox={invisibleCheckbox}
                                maxHeightPx={maxHeightList}
                            />
                        ))}
                    </Box>
                    {componentAdd}
                </Box>
                {!noSubmitButton ? (
                    <Box
                        className="actions"
                        sx={{
                            width: '100%',
                            marginTop: '30px',
                            display: 'flex',
                            flexDirection: 'column',
                            gap: '8px',
                        }}
                    >
                        {submitActions && submitActions.length > 0 ? (
                            submitActions.map((action) => (
                                <FlexyButton
                                    key={action.label}
                                    type="button"
                                    className="submit-btn"
                                    variant="contained"
                                    disabled={selectedValues.length === 0 || loadingAction !== null}
                                    onClick={() => {
                                        setLoadingAction(action.label);
                                        action
                                            .onSubmit({
                                                namegroup: 'selectionsociete',
                                                idsSociete: selectedValues,
                                            })
                                            .finally(() => setLoadingAction(null));
                                    }}
                                    sx={{
                                        width: '100%',
                                        padding: '15px',
                                        fontSize: '1rem',
                                        borderRadius: '15px',
                                    }}
                                >
                                    {loadingAction === action.label ? (
                                        <CircularProgress
                                            size={20}
                                            color="inherit"
                                        />
                                    ) : (
                                        action.label
                                    )}
                                </FlexyButton>
                            ))
                        ) : (
                            <FlexyButton
                                type="submit"
                                className="submit-btn"
                                variant="contained"
                                data-test-id="submit-create-group"
                                disabled={selectedValues.length === 0}
                                sx={{
                                    width: '100%',
                                    padding: '15px',
                                    fontSize: '1rem',
                                    borderRadius: '15px',
                                }}
                            >
                                {submitButtonLabel || 'Continuer'}
                            </FlexyButton>
                        )}
                    </Box>
                ) : null}
            </form>
        </Box>
    );
};
